import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  CpuChipIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  LightBulbIcon,
  BeakerIcon,
  SparklesIcon,
  ScaleIcon,
  TrophyIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Badge from '../components/common/Badge';
import CreateEditModelModal from '../components/models/CreateEditModelModal';
import PremiumGateModal from '../components/common/PremiumGateModal';
import customModelService from '../services/customModelService';
import { useAuth } from '../contexts/AuthContext';

export default function Models() {
  const navigate = useNavigate();
  const { currentUser, userProfile } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if user is premium
  const isPremium = userProfile?.subscription_tier === 'premium' || userProfile?.subscription_tier === 'pro';

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate('/login', { state: { from: '/models' } });
    }
  }, [currentUser, navigate]);

  // Load models from API on mount
  useEffect(() => {
    if (isPremium) {
      loadModels();
    } else {
      setLoading(false);
    }
  }, [isPremium]);

  const loadModels = async () => {
    try {
      setLoading(true);
      const data = await customModelService.getModels();
      setModels(data);
    } catch (error) {
      console.error('Error loading models:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = () => {
    setSelectedModel(null);
    setShowModal(true);
  };

  const handleEditModel = (model) => {
    setSelectedModel(model);
    setShowModal(true);
  };

  const handleDuplicateModel = async (model) => {
    try {
      const modelData = {
        model_name: `${model.model_name} (Copy)`,
        sport: model.sport,
        description: model.description,
        ai_provider: model.ai_provider,
        weights: model.weights
      };
      await customModelService.createModel(modelData);
      await loadModels();
    } catch (error) {
      console.error('Error duplicating model:', error);
      alert(error.response?.data?.detail || 'Failed to duplicate model');
    }
  };

  const handleDeleteModel = async (modelId) => {
    if (window.confirm('Are you sure you want to delete this model?')) {
      try {
        await customModelService.deleteModel(modelId);
        await loadModels();
      } catch (error) {
        console.error('Error deleting model:', error);
        alert(error.response?.data?.detail || 'Failed to delete model');
      }
    }
  };

  const handleSaveModel = async () => {
    await loadModels();
    setShowModal(false);
  };

  // Premium features list for the gate modal
  const premiumFeatures = [
    "Create unlimited custom AI prediction models",
    "Weight game attributes based on your betting strategy",
    "Choose from multiple AI providers (Knewton, Merlin)",
    "Track model accuracy and performance over time",
    "Compare your models against our default predictions",
    "Fine-tune models for specific sports: NFL, NBA, NHL, MLB",
    "Save and share your winning model configurations"
  ];

  // Sample attributes to show what's available
  const sampleAttributes = [
    { name: 'Season Record', category: 'General', description: 'Team\'s overall win-loss record' },
    { name: 'Recent Form', category: 'Momentum', description: 'Performance in last 5-10 games' },
    { name: 'Home/Away Splits', category: 'Location', description: 'Home field advantage factor' },
    { name: 'Offensive Rating', category: 'Offense', description: 'Points/goals scored efficiency' },
    { name: 'Defensive Rating', category: 'Defense', description: 'Points/goals allowed efficiency' },
    { name: 'Head-to-Head', category: 'History', description: 'Historical matchup record' },
    { name: 'Rest Days', category: 'Schedule', description: 'Days since last game' },
    { name: 'Injury Impact', category: 'Roster', description: 'Key player availability' }
  ];

  return (
    <div className="relative min-h-[calc(100vh-8rem)] space-y-6">
      {/* Premium Gate Modal - always show while feature is in development */}
      <PremiumGateModal
        isOpen={true}
        onClose={() => navigate(-1)}
        title="Custom AI Models"
        description="Create your own AI prediction models by weighting the factors that matter most to your betting strategy."
        features={premiumFeatures}
      />

      {/* Header */}
      <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gradient">My Models</h1>
            <p className="mt-2 text-text-secondary">
              Build custom prediction models by ranking the game attributes that matter most to you
            </p>
          </div>
          <button
            onClick={handleCreateModel}
            disabled={!isPremium}
            className="flex items-center gap-2 px-6 py-3 bg-accent-blue hover:bg-accent-blue/90 text-bg-primary rounded-lg font-semibold transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusIcon className="w-5 h-5" />
            Create Model
          </button>
        </div>
      </div>

      {/* Feature Highlights */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card-hover p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-accent-blue/20">
              <AdjustmentsHorizontalIcon className="w-6 h-6 text-accent-blue" />
            </div>
            <h3 className="font-semibold text-text-primary">Custom Weights</h3>
          </div>
          <p className="text-sm text-text-secondary">
            Assign importance to each factor - offense, defense, momentum, injuries, and more
          </p>
        </div>

        <div className="glass-card-hover p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-purple-500/20">
              <CpuChipIcon className="w-6 h-6 text-purple-400" />
            </div>
            <h3 className="font-semibold text-text-primary">Multiple AI Engines</h3>
          </div>
          <p className="text-sm text-text-secondary">
            Choose between Knewton (balanced) or Merlin (aggressive) AI prediction engines
          </p>
        </div>

        <div className="glass-card-hover p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-accent-green/20">
              <ChartBarIcon className="w-6 h-6 text-accent-green" />
            </div>
            <h3 className="font-semibold text-text-primary">Performance Tracking</h3>
          </div>
          <p className="text-sm text-text-secondary">
            Track each model's accuracy over time and see which strategies work best
          </p>
        </div>

        <div className="glass-card-hover p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg bg-yellow-500/20">
              <BeakerIcon className="w-6 h-6 text-yellow-400" />
            </div>
            <h3 className="font-semibold text-text-primary">A/B Testing</h3>
          </div>
          <p className="text-sm text-text-secondary">
            Create multiple models and compare their predictions side by side
          </p>
        </div>
      </div>

      {/* How Custom Models Work */}
      <div className="glass-card p-6">
        <h2 className="text-xl font-bold mb-6 flex items-center gap-2">
          <LightBulbIcon className="w-6 h-6 text-accent-blue" />
          How Custom Models Work
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-accent-blue">1</span>
            </div>
            <h3 className="font-semibold mb-2">Choose Attributes</h3>
            <p className="text-sm text-text-secondary">
              Select which game factors matter most - offense, defense, home field, recent form, injuries, etc.
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-accent-blue">2</span>
            </div>
            <h3 className="font-semibold mb-2">Set Weights</h3>
            <p className="text-sm text-text-secondary">
              Assign percentage weights to each attribute. Higher weight = more influence on predictions.
            </p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-accent-blue/20 flex items-center justify-center mx-auto mb-4">
              <span className="text-xl font-bold text-accent-blue">3</span>
            </div>
            <h3 className="font-semibold mb-2">Get Predictions</h3>
            <p className="text-sm text-text-secondary">
              Our AI uses your weights to generate personalized predictions tailored to your strategy.
            </p>
          </div>
        </div>
      </div>

      {/* Available Attributes Preview */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <ScaleIcon className="w-5 h-5 text-accent-blue" />
          Available Attributes
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {sampleAttributes.map((attr, index) => (
            <div key={index} className="glass-card-hover p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="font-medium text-text-primary">{attr.name}</span>
                <Badge variant="info" size="sm">{attr.category}</Badge>
              </div>
              <p className="text-xs text-text-secondary">{attr.description}</p>
            </div>
          ))}
        </div>
        <p className="text-sm text-text-secondary mt-4 text-center">
          And many more sport-specific attributes for NFL, NBA, NHL, and MLB...
        </p>
      </div>

      {/* Models Table - Only for premium users */}
      {isPremium && (
        <Card className="p-6">
          <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
            <SparklesIcon className="w-5 h-5 text-accent-blue" />
            Your Models
          </h2>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
            </div>
          ) : models.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🎯</div>
              <h3 className="text-2xl font-bold text-gradient mb-2">
                Create Your First Model
              </h3>
              <p className="text-text-secondary mb-6 max-w-md mx-auto">
                Build custom prediction models by ranking the game attributes that matter most to you
              </p>
              <button
                onClick={handleCreateModel}
                className="px-6 py-3 bg-accent-blue hover:bg-accent-blue/90 text-bg-primary rounded-lg font-semibold transition-colors duration-200"
              >
                Get Started
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase">Model Name</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase">Sport</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase">Created</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-text-secondary uppercase">Top Attributes</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-text-secondary uppercase">Accuracy</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-text-secondary uppercase">Win Rate</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-text-secondary uppercase">Predictions</th>
                    <th className="text-right py-3 px-4 text-xs font-medium text-text-secondary uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {models.map((model) => (
                    <tr
                      key={model.id}
                      className="border-b border-border hover:bg-bg-secondary/30 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="font-semibold text-text-primary">{model.model_name}</div>
                        {model.is_default && (
                          <span className="text-xs text-accent-blue">Default</span>
                        )}
                      </td>
                      <td className="py-4 px-4">
                        <Badge variant="info" size="sm">{model.sport.toUpperCase()}</Badge>
                      </td>
                      <td className="py-4 px-4">
                        <span className="text-sm text-text-secondary">
                          {new Date(model.created_at).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex flex-wrap gap-1">
                          {model.weights
                            .sort((a, b) => b.weight - a.weight)
                            .slice(0, 3)
                            .map((w, idx) => (
                              <span
                                key={idx}
                                className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary"
                                title={`${w.weight}%`}
                              >
                                {w.attribute_key}
                              </span>
                            ))}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-text-secondary text-sm">Coming Soon</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-text-secondary text-sm">Coming Soon</span>
                      </td>
                      <td className="py-4 px-4 text-right">
                        <span className="text-text-secondary text-sm">-</span>
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditModel(model)}
                            className="p-2 hover:bg-bg-secondary rounded-lg transition-colors"
                            title="Edit Model"
                          >
                            <PencilIcon className="w-4 h-4 text-accent-blue" />
                          </button>
                          <button
                            onClick={() => handleDuplicateModel(model)}
                            className="p-2 hover:bg-bg-secondary rounded-lg transition-colors"
                            title="Duplicate Model"
                          >
                            <DocumentDuplicateIcon className="w-4 h-4 text-warning" />
                          </button>
                          <button
                            onClick={() => handleDeleteModel(model.id)}
                            className="p-2 hover:bg-bg-secondary rounded-lg transition-colors"
                            title="Delete Model"
                          >
                            <TrashIcon className="w-4 h-4 text-danger" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Example Model Strategies */}
      <div className="glass-card p-6">
        <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrophyIcon className="w-5 h-5 text-yellow-400" />
          Popular Model Strategies
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="glass-card-hover p-4">
            <h3 className="font-semibold text-accent-blue mb-2">Momentum Hunter</h3>
            <p className="text-sm text-text-secondary mb-3">
              Weights recent form and streak data heavily. Best for identifying hot teams.
            </p>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs px-2 py-1 bg-accent-blue/20 rounded text-accent-blue">Recent Form 35%</span>
              <span className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary">Streak 25%</span>
              <span className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary">H2H 20%</span>
            </div>
          </div>
          <div className="glass-card-hover p-4">
            <h3 className="font-semibold text-accent-green mb-2">Defense First</h3>
            <p className="text-sm text-text-secondary mb-3">
              Prioritizes defensive metrics and low-scoring games. Great for totals betting.
            </p>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs px-2 py-1 bg-accent-green/20 rounded text-accent-green">Defense 40%</span>
              <span className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary">Goalie/Pitching 30%</span>
              <span className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary">Rest 15%</span>
            </div>
          </div>
          <div className="glass-card-hover p-4">
            <h3 className="font-semibold text-purple-400 mb-2">Home Warrior</h3>
            <p className="text-sm text-text-secondary mb-3">
              Emphasizes home field advantage and travel factors. Ideal for spread bets.
            </p>
            <div className="flex flex-wrap gap-1">
              <span className="text-xs px-2 py-1 bg-purple-500/20 rounded text-purple-400">Home/Away 35%</span>
              <span className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary">Rest Days 25%</span>
              <span className="text-xs px-2 py-1 bg-bg-secondary rounded text-text-secondary">Record 20%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Create/Edit Model Modal */}
      {showModal && (
        <CreateEditModelModal
          model={selectedModel}
          onSave={handleSaveModel}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
}
